import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import PublicAssessmentRecording from '@/components/assessment/PublicAssessmentRecording';
import AssessmentSummary from '@/components/assessment/AssessmentSummary';
import PerformanceComparison from '@/components/assessment/PerformanceComparison';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Star, FileText } from 'lucide-react';
import Logo from '@/components/Logo';
import { decodeId } from '@/utils/idEncoder';

interface TranscriptEntry {
  message: string;
  timestamp?: string;
  source?: string;
}

const PublicCandidateView: React.FC = () => {
  const { id: encodedId } = useParams<{ id: string }>();
  const [candidate, setCandidate] = useState<any>(null);
  const [candidateAssessment, setCandidateAssessment] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [comparisonData, setComparisonData] = React.useState<{
    averageScore: number;
    totalCandidates: number;
    rank: number;
    percentile: number;
  }>({
    averageScore: 0,
    totalCandidates: 0,
    rank: 0,
    percentile: 0
  });

  useEffect(() => {
    if (!encodedId) return;
    
    const decodedId = decodeId(encodedId);
    if (!decodedId) {
      toast.error('Invalid candidate ID');
      return;
    }
    
    fetchCandidateDetails(decodedId);
  }, [encodedId]);

  const fetchCandidateDetails = async (candidateId: string) => {
    try {
      // Fetch candidate data with organization info
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select(`
          *,
          ai_rating,
          skill_ratings,
          assessments!inner (
            id,
            title,
            organizations!inner (
              name
            )
          )
        `)
        .eq('id', candidateId)
        .limit(1)
        .single();

      if (candidateError) throw candidateError;

      if (!candidateData) {
        toast.error('Candidate not found');
        return;
      }

      // Fetch assessment data if assessment is completed
      if (candidateData.status === 'completed' && candidateData.assessment_id) {
        const { data: assessmentData, error: assessmentError } = await supabase
          .from('candidate_assessments')
          .select('*')
          .eq('candidate_id', candidateId)
          .eq('assessment_id', candidateData.assessment_id)
          .limit(1)
          .maybeSingle();

        if (assessmentError) throw assessmentError;

        if (assessmentData) {
          // Get the public URL for the audio file
          const audioPath = `${candidateData.assessment_id}/${candidateId}/conversation.mp3`;
          const { data: { publicUrl } } = supabase.storage
            .from('assessment-recordings')
            .getPublicUrl(audioPath);

          // Add cache-busting parameter to ensure we get the latest file
          const cacheBuster = `?v=${Date.now()}`;
          const audioUrlWithCacheBusting = publicUrl + cacheBuster;

          setCandidateAssessment({
            ...assessmentData,
            audio_url: audioUrlWithCacheBusting
          });
        }
      }

      // Set candidate data
      setCandidate({
        ...candidateData,
        assessmentTitle: candidateData.assessments.title,
        organizationName: candidateData.assessments.organizations.name
      });
    } catch (error: any) {
      console.error('Error fetching candidate details:', error);
      toast.error('Failed to load candidate details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!candidate?.assessment_id || !candidate?.ai_rating) return;

    const fetchComparisonData = async () => {
      try {
        // Calculate current candidate's score
        const currentScore = candidate.ai_rating * 20;

        // Fetch all completed candidates for this assessment
        const { data: candidates, error } = await supabase
          .from('candidates')
          .select('id, score, ai_rating')
          .eq('assessment_id', candidate.assessment_id)
          .eq('status', 'completed')
          .not('id', 'eq', candidate.id); // Exclude current candidate

        if (error) throw error;

        if (!candidates?.length) {
          setComparisonData({
            averageScore: currentScore,
            totalCandidates: 1,
            rank: 1,
            percentile: 100
          });
          return;
        }

        // Calculate metrics
        const scores = candidates.map((c: any) => (c.ai_rating || 0) * 20);
        const allScores = [...scores, currentScore].sort((a: number, b: number) => b - a);
        
        const averageScore = scores.reduce((acc: number, score: number) => acc + score, 0) / scores.length;
        const rank = allScores.indexOf(currentScore) + 1;
        const totalCandidates = allScores.length;
        const percentile = ((totalCandidates - rank + 1) / totalCandidates) * 100;

        setComparisonData({
          averageScore: Math.round(averageScore * 10) / 10, // Round to 1 decimal place
          totalCandidates,
          rank,
          percentile
        });
      } catch (error) {
        console.error('Error fetching comparison data:', error);
        const currentScore = candidate.ai_rating * 20;
        // Fallback to default values on error
        setComparisonData({
          averageScore: currentScore,
          totalCandidates: 1,
          rank: 1,
          percentile: 100
        });
      }
    };

    fetchComparisonData();
  }, [candidate?.assessment_id, candidate?.ai_rating]);

  const handlePlayPause = () => setIsPlaying(!isPlaying);
  const handleVolumeToggle = () => setIsMuted(!isMuted);
  const handleSeek = (value: number[]) => {
    if (value.length > 0) {
      setCurrentTime(value[0]);
    }
  };
  const handleOpenTranscript = () => {};

  // Add helper function to calculate score
  const calculateScore = (rating: number) => Math.round(rating * 20);

  if (!encodedId || !decodeId(encodedId)) {
    return <Navigate to="/" />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Candidate Not Found</h1>
          <p className="text-muted-foreground">The requested candidate could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-[#F8F7FF]">
      {/* Header with Logo */}
      <header className="w-full border-b bg-white/80 backdrop-blur-md">
        <div className="container mx-auto flex h-16 items-center px-4">
          <Logo size="md" />
          <div className="flex-1" />
          <div className="text-right">
            <p className="text-sm font-medium">{candidate.organizationName}</p>
            <p className="text-sm text-muted-foreground">Assessment Results</p>
          </div>
        </div>
      </header>

      <div className="container mx-auto p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Assessment Title */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-roundone-purple">{candidate.assessmentTitle}</h1>
            <p className="text-muted-foreground mt-2">Candidate Assessment Results</p>
          </div>

          {/* Candidate Header */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <Avatar className="h-16 w-16">
                  <AvatarFallback className="bg-roundone-purple/10 text-roundone-purple text-lg">
                    {candidate.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h1 className="text-2xl font-bold">{candidate.name}</h1>
                  <p className="text-muted-foreground">{candidate.email}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Badge variant="outline" className={`${
                      candidate.status === 'completed' ? 'bg-green-100 text-green-800' :
                      candidate.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {candidate.status === 'completed' ? 'Completed' :
                       candidate.status === 'in_progress' ? 'In Progress' :
                       'Invited'}
                    </Badge>
                    {candidate.completed_date && (
                      <span className="text-sm text-muted-foreground">
                        Completed on {new Date(candidate.completed_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assessment Content */}
          {candidate.status === 'completed' && (
            <div className="space-y-6">
              {/* Audio Recording */}
              {candidateAssessment?.audio_url && (
                <PublicAssessmentRecording
                  assessmentTitle={candidate.assessmentTitle}
                  audioUrl={candidateAssessment.audio_url}
                  isPlaying={isPlaying}
                  isMuted={isMuted}
                  currentTime={currentTime}
                  duration={duration}
                  handlePlayPause={handlePlayPause}
                  handleVolumeToggle={handleVolumeToggle}
                  handleSeek={handleSeek}
                  setDuration={setDuration}
                  setCurrentTime={setCurrentTime}
                />
              )}

              {/* Assessment Summary and Performance Comparison side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Assessment Summary */}
                <Card className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Assessment Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h3 className="font-medium mb-2">AI Summary</h3>
                      <p className="text-sm text-muted-foreground">
                        {candidate.ai_summary || "The candidate's performance could not be evaluated based on this transcript as it lacks the candidate's responses related to Agile Methodologies or any other skill."}
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium mb-2">AI Rating</h3>
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`h-5 w-5 ${
                              star <= Math.round(candidate.ai_rating || 0)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-medium mb-2">Skill Ratings</h3>
                      {candidate.skill_ratings?.map((skill: any, index: number) => (
                        <div key={index} className="mb-2">
                          <div className="flex justify-between text-sm">
                            <span>{skill.skill}</span>
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-4 w-4 ${
                                    star <= Math.round(skill.rating)
                                      ? 'text-yellow-400 fill-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Performance Comparison Card */}
                <Card className="overflow-hidden">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Performance Comparison</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium">Candidate Score</span>
                          <span className="text-sm">{(candidate.ai_rating * 20).toFixed(1)}%</span>
                        </div>
                        <div className="h-2 bg-secondary rounded-none overflow-hidden">
                          <div 
                            className="h-full bg-roundone-purple" 
                            style={{ width: `${candidate.ai_rating * 20}%` }}
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium">Average score of other top candidates</span>
                          <span className="text-sm">{comparisonData.averageScore.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 bg-secondary rounded-none overflow-hidden">
                          <div 
                            className="h-full bg-roundone-purple/50" 
                            style={{ width: `${comparisonData.averageScore}%` }}
                          />
                        </div>
                      </div>

                      <div className="pt-2">
                        <p className="text-sm">
                          Ranked <span className="font-medium">#{comparisonData.rank}</span> out of{' '}
                          <span className="font-medium">{comparisonData.totalCandidates}</span> candidates
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Top {comparisonData.percentile.toFixed(1)}% percentile
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Transcript Section - Full Width */}
              <Card className="overflow-hidden">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Assessment Transcript</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6 max-h-[800px] overflow-y-auto pr-4">
                    {candidateAssessment?.transcript ? (
                      <div className="space-y-6">
                        {Array.isArray(candidateAssessment.transcript) ? (
                          candidateAssessment.transcript.map((entry: TranscriptEntry, index: number) => (
                            <div key={index} 
                              className={`flex items-start space-x-4 ${
                                entry.source === 'ai' ? '' : 'flex-row-reverse space-x-reverse'
                              }`}
                            >
                              <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0">
                                {entry.source === 'ai' ? (
                                  <div className="bg-purple-100 w-8 h-8 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-medium text-roundone-purple">AI</span>
                                  </div>
                                ) : (
                                  <div className="bg-blue-100 w-8 h-8 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-medium text-blue-600">
                                      {candidate.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className={`flex-1 space-y-1 ${
                                entry.source === 'ai' ? '' : 'text-right'
                              }`}>
                                <p className={`text-sm leading-relaxed inline-block px-4 py-2 rounded-lg ${
                                  entry.source === 'ai' 
                                    ? '' 
                                    : 'bg-[#F3F1FF]'
                                }`}>
                                  {entry.message}
                                </p>
                                {entry.timestamp && (
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(entry.timestamp).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric'
                                    })} at {new Date(entry.timestamp).toLocaleTimeString([], {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted-foreground text-sm">{candidateAssessment.transcript}</p>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-muted-foreground">Transcript not available</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t bg-white/80 backdrop-blur-md mt-12">
        <div className="container mx-auto py-4 px-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Powered by <span className="text-roundone-purple font-medium">RoundOne AI</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicCandidateView; 